/**
 * Pagina per visualizzare e gestire tutti i Tenant (Agency)
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAgencies, updateAgency, deleteAgency, Agency, UpdateAgencyData } from "../api/agencyApi";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import { useNotification } from "../context/NotificationContext";

// Modal per la modifica
interface EditModalProps {
  agency: Agency | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, data: UpdateAgencyData) => Promise<void>;
}

const EditModal: React.FC<EditModalProps> = ({ agency, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<UpdateAgencyData>({
    nome: "",
    descrizione: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (agency) {
      setFormData({
        nome: agency.nome,
        descrizione: agency.descrizione
      });
    }
  }, [agency]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (agency) {
      setLoading(true);
      await onSave(agency.id, formData);
      setLoading(false);
    }
  };

  if (!isOpen || !agency) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Modifica Tenant
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome
              </label>
              <input
                type="text"
                value={formData.nome || ""}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
                minLength={2}
                maxLength={100}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrizione
              </label>
              <textarea
                value={formData.descrizione || ""}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                required
                maxLength={500}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={loading}
                onClick={() => handleSubmit}
              >
                {loading ? "Salvataggio..." : "Salva"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Modal di conferma eliminazione
interface DeleteModalProps {
  agency: Agency | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ agency, isOpen, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen || !agency) return null;

  const handleDelete = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-center mb-4">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <span className="text-red-600 text-xl">⚠️</span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Conferma Eliminazione
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            Sei sicuro di voler eliminare il tenant <strong>{agency.nome}</strong>?
            <br />
            <span className="text-red-600 mt-2 block">
              I contenuti associati verranno marcati come orfani.
            </span>
          </p>
          <div className="flex justify-center space-x-2">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Annulla
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? "Eliminazione..." : "Elimina"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TenantsPage: React.FC = () => {
  const { addNotification } = useNotification();
  
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  
  const [sortBy, setSortBy] = useState("nome");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Stati per i modal
  const [editModal, setEditModal] = useState<{ isOpen: boolean; agency: Agency | null }>({
    isOpen: false,
    agency: null
  });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; agency: Agency | null }>({
    isOpen: false,
    agency: null
  });

  const loadAgencies = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getAgencies(page, pagination.limit, sortBy, sortOrder);
      
      if (response.success) {
        setAgencies(response.data);
        setPagination(response.pagination);
      } else {
        addNotification("error", response.error || "Errore nel caricamento dei tenant");
      }
    } catch (error: any) {
      console.error("Errore nel caricamento tenant:", error);
      addNotification("error", "Errore nel caricamento dei tenant");
      setAgencies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgencies(1);
  }, [sortBy, sortOrder]);

  const handleSortChange = (newSortBy: string) => {
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      loadAgencies(newPage);
    }
  };

  const handleEdit = (agency: Agency) => {
    setEditModal({ isOpen: true, agency });
  };

  const handleSaveEdit = async (id: string, data: UpdateAgencyData) => {
    try {
      const response = await updateAgency(id, data);
      if (response.success) {
        addNotification("success", "Tenant aggiornato con successo");
        setEditModal({ isOpen: false, agency: null });
        loadAgencies(pagination.page);
      } else {
        addNotification("error", response.error || "Errore nell'aggiornamento");
      }
    } catch (error: any) {
      addNotification("error", error.message || "Errore nell'aggiornamento del tenant");
    }
  };

  const handleDelete = (agency: Agency) => {
    setDeleteModal({ isOpen: true, agency });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.agency) {
      try {
        const response = await deleteAgency(deleteModal.agency.id);
        if (response.success) {
          addNotification("success", "Tenant eliminato con successo");
          setDeleteModal({ isOpen: false, agency: null });
          loadAgencies(pagination.page);
        } else {
          addNotification("error", response.error || "Errore nell'eliminazione");
        }
      } catch (error: any) {
        addNotification("error", error.message || "Errore nell'eliminazione del tenant");
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getSortIcon = (columnName: string) => {
    if (columnName !== sortBy) {
      return "↕️";
    }
    return sortOrder === "asc" ? "↗️" : "↙️";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="flex flex-col items-center space-y-3">
          <svg
            className="animate-spin h-8 w-8 text-purple-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-600">Caricamento tenant...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Gestione Tenant"
        subtitle="Visualizza e gestisci tutti i tenant (agency) del sistema"
        actions={
          <Link to="/tenants/add">
            <Button className="flex items-center" onClick={() => {}}>
              <span className="mr-2">➕</span>
              Aggiungi Tenant
            </Button>
          </Link>
        }
      />

      {/* Statistiche */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🏢</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Totale Tenant</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">📄</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pagina Corrente</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.page} / {pagination.totalPages}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">📊</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Ordinamento</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {sortBy} {getSortIcon(sortBy)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabella Tenant */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {agencies.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-4xl mb-4 block">🏢</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun tenant trovato
            </h3>
            <p className="text-gray-600 mb-4">
              Non ci sono tenant registrati nel sistema.
            </p>
            <Link to="/tenants/add">
              <Button onClick={() => {}}>
                <span className="mr-2">➕</span>
                Aggiungi il primo tenant
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSortChange("nome")}
                    >
                      Nome {getSortIcon("nome")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrizione
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSortChange("dataIscrizione")}
                    >
                      Data Iscrizione {getSortIcon("dataIscrizione")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agencies.map((agency) => (
                    <tr 
                      key={agency.id} 
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-purple-600 font-semibold text-sm">
                                {agency.nome.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {agency.nome}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {agency.id && agency.id.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {agency.descrizione.length > 100 
                            ? `${agency.descrizione.substring(0, 100)}...` 
                            : agency.descrizione
                          }
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(agency.dataIscrizione)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(agency.createdAt)}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                          Attivo
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleEdit(agency)}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            title="Modifica"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifica
                          </button>
                          <button
                            onClick={() => handleDelete(agency)}
                            className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                            title="Elimina"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginazione */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Precedente
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Successiva
                  </Button>
                </div>
                
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{" "}
                      <span className="font-medium">
                        {((pagination.page - 1) * pagination.limit) + 1}
                      </span>{" "}
                      -{" "}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{" "}
                      di{" "}
                      <span className="font-medium">{pagination.total}</span>{" "}
                      risultati
                    </p>
                  </div>
                  
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <span>‹</span>
                      </button>
                      
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.page
                                ? "z-10 bg-purple-50 border-purple-500 text-purple-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <span>›</span>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal di modifica */}
      <EditModal
        agency={editModal.agency}
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, agency: null })}
        onSave={handleSaveEdit}
      />

      {/* Modal di conferma eliminazione */}
      <DeleteModal
        agency={deleteModal.agency}
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, agency: null })}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default TenantsPage;